import { useI18n } from "i18n";
import AppBarContainer from "modules/app/menu/AppBarContainer";
import { PageNotFound } from "modules/app/pages/PageNotFound";
import useSystemClock from "modules/system/hooks/useSystemClock";
import { useEffect, useState } from "react";
import { EventAvailable } from "@remo-co/ui-core/src/icons/EventAvailable";
import { AddToCalendar } from "modules/eventLanding/components/AddToCalendar";
import classNames from "classnames";
import PageHelmet from "modules/pageSetting/PageHelmet";
import EventShareIcon from "modules/eventLanding/EventShareIcon/EventShareIcon";
import { useCheckEventStatus } from "modules/event/hooks/useCheckEventStatus";
import { useTheme } from "@remo-co/ui-core/src/hooks/useTheme";
import { useMediaQuery } from "@remo-co/ui-core/src/hooks/useMediaQuery";
import { PageLoader } from "@remo-co/ui-core/src/components/PageLoader";
import { Container } from "@remo-co/ui-core/src/components/Container";
import { ScrollableView } from "@remo-co/ui-core/src/components/ScrollableView";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { Button } from "@remo-co/ui-core/src/components/Button";
import { selectEnableAgencyWhiteLabelling } from "modules/settings/redux/selectors";
import { useSelector } from "react-redux";
import { Footer } from "modules/eventLanding/Footer";
import { ContactHost } from "modules/contactHost/components/ContactHost";
import { ContactHostOrigin } from "modules/contactHost/constants";
import { BuyTickets } from "modules/ticketing/components/BuyTickets";
import { selectIsMobile } from "modules/deviceInfo";
import Card from "../LandingPageCard";
import { EventCoverArt } from "../../EventCoverArt";
import { EventDescription } from "../../EventDescription";
import { EventOrganizer } from "../../EventOrganizer";
import { useEventContext } from "../../hooks/useEventContext";
import {
  Actions,
  useEventLandingPageParameters,
} from "../../hooks/useEventLandingPageParameters";
import EventDetailsContainer from "../../EventDetailsContainer";
import { GridArea, useStyles } from "./styles";
import logo from "../../assets/eventsLogo.svg";

const EventLandingPageContainer = (): JSX.Element => {
  const { t } = useI18n(["eventLanding"]);
  const { event: eventData, isEventDataLoading } = useEventContext();
  const { checkIfEventEnded } = useCheckEventStatus();
  const [showCalendarDialog, setShowCalendarDialog] = useState(false);
  const { serverTime } = useSystemClock();
  const { action } = useEventLandingPageParameters();
  const enableAgencyWhiteLabelling = useSelector(
    selectEnableAgencyWhiteLabelling,
  );
  const isMobileView = useSelector(selectIsMobile);

  const [registerCountdownHeight, setRegisterCountdownHeight] = useState(0);

  useEffect(() => {
    const containerId = "register-countdown-container";
    // eslint-disable-next-line consistent-return
    const mutationObserver = new MutationObserver(() => {
      const registerCountdownElement = document.getElementById(containerId);
      if (registerCountdownElement) {
        const resizeObserver = new ResizeObserver(() => {
          const newHeight = registerCountdownElement.offsetHeight;
          if (newHeight !== registerCountdownHeight) {
            setRegisterCountdownHeight(newHeight);
          }
        });

        resizeObserver.observe(registerCountdownElement);
        return () => resizeObserver.disconnect();
      }
    });

    mutationObserver.observe(document.body, { childList: true, subtree: true });

    return () => mutationObserver.disconnect();
  }, [registerCountdownHeight]);

  const classes = useStyles();
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down("md"));

  let companyName: string | undefined;
  let logoURL: string | undefined;

  const handleAddToCalendarDialogClose = () => {
    setShowCalendarDialog(false);
  };

  if (typeof eventData?.company !== "string") {
    companyName = eventData?.company?.name;
    logoURL = eventData?.company?.logoURL;
  }

  if (isEventDataLoading) {
    return <PageLoader message={t("eventLanding:loading.events.detail")} />;
  }

  if (eventData === null) {
    return <PageNotFound />;
  }

  return (
    <>
      <PageHelmet title={eventData?.name} />
      <BuyTickets />
      <AppBarContainer
        hideSigInButton
        hideTitle
        logoImage={<img src={logo} alt="logo" className={classes.logoImage} />}
      />
      <ScrollableView
        withAppBar
        style={{
          height: isMobileView
            ? `calc(100% - ${registerCountdownHeight + 64}px)`
            : "calc(100% - 64px)",
        }}
      >
        <Container className={classes.root} maxWidth="xl">
          <div className={classes.container}>
            <Card
              style={{
                gridArea: GridArea.coverart,
                padding: 0,
              }}
              elevation={0}
            >
              <EventCoverArt
                style={{
                  maxHeight: "490px",
                }}
                src={eventData.logoURL}
                rounded="xl"
              />
            </Card>
            <Container
              style={{
                gridArea: GridArea.details,
                minWidth: "336px",
              }}
            >
              <EventDetailsContainer
                currentTime={serverTime}
                eventData={eventData}
                isEventFull={action === Actions.FULL}
                setShowCalendarDialog={setShowCalendarDialog}
              />
            </Container>
            <Card
              flex
              elevation={0}
              size={matches ? "sm" : "md"}
              innerClassName={classes.organizeInner}
              className={classes.organizeCard}
              style={{
                gridArea: GridArea.organizer,
              }}
              justifyContent="space-between"
            >
              {companyName && (
                <Container>
                  <EventOrganizer name={companyName} logoURL={logoURL} />
                </Container>
              )}
              {eventData && !checkIfEventEnded(eventData) && (
                <Container
                  flex
                  justifyContent="flex-end"
                  className={classNames(classes.shareContainer, {
                    [classes.shareContainerNoSharer]: eventData.isPrivate,
                  })}
                >
                  <ContactHost
                    origin={ContactHostOrigin.EVENT_LANDING}
                    event={eventData}
                  />
                  <Button
                    onClick={() => setShowCalendarDialog(true)}
                    variant="tertiary"
                    color="dark"
                    size="md"
                    className={classes.actionButtons}
                    contentWrapperClassName={classes.actionButtonsWrapper}
                  >
                    <EventAvailable className={classes.actionButtonIcons} />
                    <Typography
                      variant="button"
                      className={classes.actionButtonsWrapper}
                    >
                      {t("eventLanding:add.calendar")}
                    </Typography>
                  </Button>
                  {!eventData.isPrivate && <EventShareIcon event={eventData} />}
                </Container>
              )}
            </Card>
            {/* Comparing to "<br/>" is because of how the text editor works when it is empty */}
            {eventData?.description && eventData.description !== "<br/>" && (
              <Container
                style={{
                  gridArea: GridArea.description,
                }}
              >
                <EventDescription description={eventData?.description} />
              </Container>
            )}
          </div>
          <AddToCalendar
            open={showCalendarDialog}
            event={eventData}
            onClose={handleAddToCalendarDialogClose}
          />
        </Container>
        {!enableAgencyWhiteLabelling && <Footer />}
      </ScrollableView>
    </>
  );
};

export default EventLandingPageContainer;
